import React, { useEffect, useMemo, useState } from 'react';
import {
  Tabs, Badge, Space, Card, Table, Tag, Button, Modal, Form,
  Input, Select, Empty, Row, Col, Statistic, Typography,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  HomeOutlined, ToolOutlined, UserOutlined, FilePdfOutlined,
  MailOutlined, PlusOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { OwnerPortal } from './owner/Portal';
import { OwnerStatements } from './owner/MyStatements';
import { OwnerCorrespondence } from './owner/Correspondence';
import { VisitorManagement } from './VisitorManagement';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import type { MaintenanceRequest, VisitorAuthorization, Owner } from '../types';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// ─── Priority / Status configs ────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  low: 'default', medium: 'blue', high: 'orange', urgent: 'red',
};

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  submitted:   { color: 'blue',    label: 'Submitted' },
  in_review:   { color: 'gold',    label: 'In Review' },
  assigned:    { color: 'purple',  label: 'Assigned' },
  in_progress: { color: 'orange',  label: 'In Progress' },
  completed:   { color: 'green',   label: 'Completed' },
  cancelled:   { color: 'default', label: 'Cancelled' },
};

const CATEGORIES = [
  'Plumbing', 'Electrical', 'HVAC / Heating', 'Appliance', 'Pest Control',
  'Structural', 'Common Area', 'Noise / Disturbance', 'Elevator', 'Other',
];

// ─── Owner Maintenance Panel ──────────────────────────────────────────────────

const OwnerMaintenancePanel: React.FC = () => {
  const {
    maintenanceRequests,
    fetchMaintenanceRequests,
    createMaintenanceRequest,
    owners,
  } = useCondoStore();
  const { currentCondo, user: authUser } = useAuthStore();
  const { user } = useAuth();

  // Derive owner's unit from authStore user (has ownerId) or owners list
  const ownerRecord = useMemo(
    () => (owners as Owner[]).find(
      (o: Owner) => o.email === user?.email || o.id === authUser?.ownerId,
    ),
    [owners, user?.email, authUser?.ownerId],
  );
  const unitNumber: string = ownerRecord?.unitNumber ?? '';
  const ownerName: string = ownerRecord?.name ?? user?.name ?? '';

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchMaintenanceRequests(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchMaintenanceRequests is a stable zustand action
  }, [currentCondo?.id]);

  // Filter to this owner's unit
  const myRequests: MaintenanceRequest[] = useMemo(
    () =>
      (maintenanceRequests as MaintenanceRequest[]).filter(
        r => r.unitNumber === unitNumber || r.ownerId === authUser?.ownerId,
      ),
    [maintenanceRequests, unitNumber, authUser?.ownerId],
  );

  const openCount = myRequests.filter(
    r => r.status !== 'completed' && r.status !== 'cancelled',
  ).length;

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await createMaintenanceRequest({
        ...values,
        condoId: currentCondo?.id ?? '',
        unitNumber,
        ownerName,
        ownerId: authUser?.ownerId ?? authUser?.id ?? '',
        priority: values.priority ?? 'medium',
        status: 'submitted',
        // Sprint 145 (R14-9) — `toISOString()` is UTC and stamped tomorrow's
        // date after 20:00 EDT. The `targetDate` beside it was already correct;
        // this line simply never matched it.
        submittedDate: dayjs().format('YYYY-MM-DD'),
        targetDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
        notes: [],
      });
      staticApi.message.success('Maintenance request submitted');
      setModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      // Sprint 89A — surface server/network failures (was silent). Enum
      // normalization + requestNumber are now handled centrally in the store
      // action createMaintenanceRequest. AntD validation rejections carry
      // `errorFields` and already render inline, so skip the toast for those.
      if (!err?.errorFields) {
        staticApi.message.error(
          err?.message || 'Could not submit your request. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Request #',
      dataIndex: 'requestNumber',
      key: 'num',
      width: 120,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'cat',
      width: 140,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'prio',
      width: 100,
      render: (v: string) => (
        <Tag color={PRIORITY_COLORS[v] ?? 'default'}>{v?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => {
        const cfg = STATUS_CONFIG[v];
        return cfg ? (
          <Tag color={cfg.color}>{cfg.label}</Tag>
        ) : (
          <Tag>{v}</Tag>
        );
      },
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedDate',
      key: 'date',
      width: 120,
      render: (v: string) => dayjs(v).format('MMM D, YYYY'),
      sorter: (a: MaintenanceRequest, b: MaintenanceRequest) =>
        dayjs(a.submittedDate).unix() - dayjs(b.submittedDate).unix(),
      defaultSortOrder: 'descend' as const,
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Stats strip */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small" bordered={false} style={{ background: '#f0f5ff', border: '1px solid #d6e4ff' }}>
            <Statistic
              title="Open Requests"
              value={openCount}
              prefix={<ClockCircleOutlined style={{ color: '#2f54eb' }} />}
              valueStyle={{ color: '#2f54eb' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" bordered={false} style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Statistic
              title="Completed"
              value={myRequests.filter(r => r.status === 'completed').length}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" bordered={false}>
            <Statistic title="Total Submitted" value={myRequests.length} />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            size="small"
          >
            New Request
          </Button>
        }
        title={
          <Space>
            <ToolOutlined />
            My Maintenance Requests
            {unitNumber && (
              <Text type="secondary" style={{ fontSize: 12 }}>— Unit {unitNumber}</Text>
            )}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={myRequests}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showTotal: t => `${t} request${t !== 1 ? 's' : ''}` }}
          locale={{
            emptyText: (
              <Empty
                description="No maintenance requests yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
                  Submit First Request
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      {/* New Request Modal */}
      <Modal
        open={modalOpen}
        title={
          <Space>
            <ToolOutlined />
            Submit Maintenance Request
          </Space>
        }
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleSubmit}
        okText="Submit Request"
        confirmLoading={submitting}
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="title" label="Issue Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. Leaking faucet in bathroom" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select placeholder="Select category">
                  {CATEGORIES.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="medium">
                <Select>
                  <Option value="low">Low — no urgency</Option>
                  <Option value="medium">Medium — within a week</Option>
                  <Option value="high">High — within 48 hours</Option>
                  <Option value="urgent">Urgent — safety / water damage</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={4} placeholder="Describe the issue in detail — location, when it started, any related damage…" />
          </Form.Item>
          {unitNumber && (
            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: -8 }}>
              Request will be submitted for Unit {unitNumber}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

// ─── Main Hub ─────────────────────────────────────────────────────────────────

/**
 * S18 — My Home Hub
 * Consolidates My Unit, Maintenance Requests, Visitor Management,
 * My Statements, and My Correspondence into one tabbed hub for owners.
 *
 * Adds owner self-service maintenance request submission.
 */
const MyHomeHub: React.FC = () => {
  const { maintenanceRequests = [], visitorAuthorizations = [], owners } =
    useCondoStore();
  const { user } = useAuth();
  const { user: authUser } = useAuthStore();

  const ownerRecord = useMemo(
    () => (owners as Owner[]).find(
      (o: Owner) => o.email === user?.email || o.id === authUser?.ownerId,
    ),
    [owners, user?.email, authUser?.ownerId],
  );
  const unitNumber: string = ownerRecord?.unitNumber ?? '';

  // Badge: open maintenance requests for this unit
  const openMaintenance: number = useMemo(
    () =>
      (maintenanceRequests as MaintenanceRequest[]).filter(
        r =>
          (r.unitNumber === unitNumber || r.ownerId === authUser?.ownerId) &&
          r.status !== 'completed' &&
          r.status !== 'cancelled',
      ).length,
    [maintenanceRequests, unitNumber, authUser?.ownerId],
  );

  // Badge: active visitor authorizations for this unit
  const activeVisitors: number = useMemo(
    () =>
      (visitorAuthorizations as VisitorAuthorization[]).filter(
        v => v.status === 'ACTIVE',
      ).length,
    [visitorAuthorizations],
  );

  const makeBadgeLabel = (icon: React.ReactNode, label: string, count: number) => (
    <Space size={4}>
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <Badge count={count} size="small" style={{ backgroundColor: '#1890ff', marginLeft: 2 }} />
      )}
    </Space>
  );

  const items = [
    {
      key: 'my-unit',
      label: (
        <Space size={4}>
          <HomeOutlined />
          <span>My Unit</span>
        </Space>
      ),
      children: <OwnerPortal />,
    },
    {
      key: 'maintenance',
      label: makeBadgeLabel(<ToolOutlined />, 'Maintenance', openMaintenance),
      children: <OwnerMaintenancePanel />,
    },
    {
      key: 'visitors',
      label: makeBadgeLabel(<UserOutlined />, 'Visitors', activeVisitors),
      children: <VisitorManagement />,
    },
    {
      key: 'statements',
      label: (
        <Space size={4}>
          <FilePdfOutlined />
          <span>Statements</span>
        </Space>
      ),
      children: <OwnerStatements />,
    },
    {
      key: 'correspondence',
      label: (
        <Space size={4}>
          <MailOutlined />
          <span>Correspondence</span>
        </Space>
      ),
      children: <OwnerCorrespondence />,
    },
  ];

  return (
    <Tabs
      items={items}
      size="large"
      destroyInactiveTabPane={false}
      style={{ marginTop: -8 }}
    />
  );
};

export default MyHomeHub;
